/**
 * Parent follow-sheet import: photo/PDF of a FILLED paper "fiche de suivi" →
 * AI-extracted DRAFT → parent reviews/edits row-by-row → commits.
 *
 * Pipeline: file → POST /attachments (kind 'follow_sheet') → POST
 * /medications/sheet/extract { attachmentId } → editable review table →
 * commits through the SAME endpoints as manual logging:
 *   - POST /medications/administrations  (idempotent clientId per sheet+slot)
 *   - POST /growth                        (weights, unverified)
 *   - POST /observations                  (notes, idempotent clientId)
 *
 * SAFETY: nothing is saved until the parent confirms; every row can be
 * unchecked or edited; rows already logged for the same med+day+time are
 * pre-unchecked ("already logged"); server-side duplicate detection (409) is
 * treated as a skip, never an overwrite. AI extraction is opt-in server-side —
 * when disabled the component explains and points to manual logging.
 *
 * App-specific labels use the inline FR/EN map pattern (see ReportUpload.tsx);
 * med names and dose statuses reuse the shared t() catalog (concept.* and admin.* keys).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getCsrfToken, ApiError } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Button, Badge } from './ui.js';

// --- Inline bilingual strings ------------------------------------------------
const L = {
  title: { fr: 'Importer une fiche remplie', en: 'Import a filled sheet' },
  hint: {
    fr: 'Prenez en photo (ou choisissez un PDF de) n’importe quelle fiche de suivi remplie — médicaments, observations, poids. Les entrées détectées seront proposées pour relecture avant tout enregistrement.',
    en: 'Photograph (or pick a PDF of) any filled follow sheet — medications, observations, weight. Detected entries are proposed for review before anything is saved.',
  },
  pick: { fr: 'Prendre une photo / choisir un fichier', en: 'Take a photo / choose a file' },
  unsupportedFile: { fr: 'Type de fichier non pris en charge (PDF ou image).', en: 'Unsupported file type (PDF or image).' },
  analyze: { fr: 'Analyser la fiche', en: 'Analyze the sheet' },
  analyzing: { fr: 'Analyse de la fiche…', en: 'Analyzing the sheet…' },
  cancel: { fr: 'Annuler', en: 'Cancel' },
  aiOff: {
    fr: 'L’analyse automatique est désactivée sur ce serveur. Enregistrez les doses manuellement dans « Aujourd’hui ».',
    en: 'Automatic analysis is disabled on this server. Log doses manually in “Today”.',
  },
  aiPrivacy: {
    fr: 'La photo est analysée par un service d’IA (Anthropic) configuré par votre médecin.',
    en: 'The photo is analyzed by an AI service (Anthropic) configured by your doctor.',
  },
  nothingFound: {
    fr: 'Rien de lisible n’a été détecté sur cette fiche. Vérifiez la photo (nette, bien éclairée) ou saisissez manuellement.',
    en: 'Nothing legible was detected on this sheet. Check the photo (sharp, well lit) or enter manually.',
  },
  reviewTitle: { fr: 'Vérifiez avant d’enregistrer', en: 'Review before saving' },
  reviewHint: {
    fr: 'Décochez ou corrigez ce qui est inexact. Rien n’est enregistré avant votre confirmation.',
    en: 'Uncheck or correct anything inaccurate. Nothing is saved until you confirm.',
  },
  medsSection: { fr: 'Doses de médicaments', en: 'Medication doses' },
  weightsSection: { fr: 'Poids', en: 'Weights' },
  notesSection: { fr: 'Observations', en: 'Notes' },
  unmatchedSection: { fr: 'Non reconnu (à saisir manuellement)', en: 'Not recognized (enter manually)' },
  alreadyLogged: { fr: 'déjà enregistré', en: 'already logged' },
  futureDate: { fr: 'date future', en: 'future date' },
  time: { fr: 'Heure', en: 'Time' },
  dose: { fr: 'Dose', en: 'Dose' },
  note: { fr: 'Note', en: 'Note' },
  save: { fr: 'Enregistrer la sélection', en: 'Save selection' },
  saving: { fr: 'Enregistrement…', en: 'Saving…' },
  savedSummary: { fr: 'enregistrée(s)', en: 'saved' },
  skippedSummary: { fr: 'déjà présente(s)', en: 'already present' },
  failedSummary: { fr: 'échec(s)', en: 'failed' },
  startOver: { fr: 'Importer une autre fiche', en: 'Import another sheet' },
  weightKg: { fr: 'kg', en: 'kg' },
} as const;

type Lang = 'fr' | 'en';

// --- Server draft shapes (mirror sheet-extractor.ts) --------------------------
interface DraftAdministration {
  orderId: string;
  medicationLabel: string;
  date: string;
  time?: string;
  status: string;
  doseGiven?: string;
  doseUnit?: string;
  note?: string;
}
interface DraftWeight {
  date: string;
  weightGrams: number;
  note?: string;
}
interface DraftObservation {
  date: string;
  text: string;
}
interface DraftUnmatched {
  medication: string;
  date?: string;
  reason: string;
}
interface FollowSheetDraft {
  extracted: boolean;
  sheetKind: string;
  administrations: DraftAdministration[];
  weights: DraftWeight[];
  observations: DraftObservation[];
  unmatched: DraftUnmatched[];
  periodFrom?: string;
  periodTo?: string;
}
interface ExtractResponse {
  draft: FollowSheetDraft;
  meds: { orderId: string; concept: string; brandName: string | null }[];
}
interface ExistingAdministration {
  orderId: string;
  scheduledDate: string;
  scheduledTime: string | null;
}
interface GrowthRow {
  date: string;
}

// Statuses a parent can pick for a sheet-imported dose.
const EDITABLE_STATUSES = ['taken', 'late', 'missed', 'vomited_after_dose', 'partial', 'refused', 'unknown'] as const;

// --- Editable review-row state -------------------------------------------------
interface AdminRow extends DraftAdministration {
  key: string;
  checked: boolean;
  duplicate: boolean;
}
interface WeightRow extends DraftWeight {
  key: string;
  checked: boolean;
  duplicate: boolean;
}
interface ObsRow extends DraftObservation {
  key: string;
  checked: boolean;
}

type Phase = 'pick' | 'review' | 'done';

export function SheetImport({ className }: { className?: string }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const lang: Lang = locale === 'en' ? 'en' : 'fr';
  const tr = (m: { fr: string; en: string }) => m[lang];

  const [phase, setPhase] = useState<Phase>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [meds, setMeds] = useState<ExtractResponse['meds']>([]);
  const [unmatched, setUnmatched] = useState<DraftUnmatched[]>([]);
  const [adminRows, setAdminRows] = useState<AdminRow[]>([]);
  const [weightRows, setWeightRows] = useState<WeightRow[]>([]);
  const [obsRows, setObsRows] = useState<ObsRow[]>([]);
  const [result, setResult] = useState<{ saved: number; skipped: number; failed: number } | null>(null);

  const medLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of meds) map.set(m.orderId, `${t(`concept.${m.concept}`)}${m.brandName ? ` (${m.brandName})` : ''}`);
    return (orderId: string, fallback: string) => map.get(orderId) ?? fallback;
  }, [meds, t]);

  function acceptFile(f: File | undefined | null) {
    if (!f) return;
    const ok = f.type === 'application/pdf' || f.type.startsWith('image/');
    if (!ok) {
      setFileError(tr(L.unsupportedFile));
      return;
    }
    setFileError(null);
    setFile(f);
  }

  function resetAll() {
    setPhase('pick');
    setFile(null);
    setFileError(null);
    setAttachmentId(null);
    setMeds([]);
    setUnmatched([]);
    setAdminRows([]);
    setWeightRows([]);
    setObsRows([]);
    setResult(null);
    extractM.reset();
    saveM.reset();
  }

  // --- Upload + extract + pre-dedupe against existing logs --------------------
  const extractM = useMutation({
    mutationFn: async (f: File) => {
      // 1. Upload the sheet (encrypted at rest server-side).
      const fd = new FormData();
      fd.append('file', f);
      fd.append('kind', 'follow_sheet');
      const res = await fetch('/api/attachments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf-token': getCsrfToken() ?? '' },
        body: fd,
      });
      const text = await res.text();
      const json = text ? JSON.parse(text) : undefined;
      if (!res.ok) {
        const err = json?.error ?? {};
        throw new ApiError(res.status, err.code ?? 'upload_failed', err.message ?? res.statusText, err.details);
      }
      const attId: string = json.attachment.id;

      // 2. Extract a draft.
      const extracted = await api.post<ExtractResponse>('/medications/sheet/extract', { attachmentId: attId });

      // 3. Pre-dedupe: what is already logged over the draft's date span?
      const dates = [
        ...extracted.draft.administrations.map((a) => a.date),
        ...extracted.draft.weights.map((w) => w.date),
      ].sort();
      let existing: ExistingAdministration[] = [];
      let existingWeights: GrowthRow[] = [];
      if (dates.length > 0) {
        const from = dates[0]!;
        const to = dates[dates.length - 1]!;
        const [adminsRes, growthRes] = await Promise.all([
          api.get<{ administrations: ExistingAdministration[] }>(`/medications/administrations?from=${from}&to=${to}`),
          api.get<{ growth: GrowthRow[] }>('/growth'),
        ]);
        existing = adminsRes.administrations;
        existingWeights = growthRes.growth;
      }
      return { attId, extracted, existing, existingWeights };
    },
    onSuccess: ({ attId, extracted, existing, existingWeights }) => {
      setAttachmentId(attId);
      setMeds(extracted.meds);
      setUnmatched(extracted.draft.unmatched);
      const adminKey = (orderId: string, date: string, time: string | null | undefined) => `${orderId}|${date}|${time ?? ''}`;
      const logged = new Set(existing.map((a) => adminKey(a.orderId, a.scheduledDate, a.scheduledTime)));
      const loggedNoTime = new Set(existing.map((a) => adminKey(a.orderId, a.scheduledDate, undefined)));
      setAdminRows(
        extracted.draft.administrations.map((a, i) => {
          const duplicate = a.time ? logged.has(adminKey(a.orderId, a.date, a.time)) : loggedNoTime.has(adminKey(a.orderId, a.date, undefined));
          return { ...a, key: `a${i}`, duplicate, checked: !duplicate };
        }),
      );
      const weightDates = new Set(existingWeights.map((g) => g.date));
      setWeightRows(
        extracted.draft.weights.map((w, i) => {
          const duplicate = weightDates.has(w.date);
          return { ...w, key: `w${i}`, duplicate, checked: !duplicate };
        }),
      );
      setObsRows(extracted.draft.observations.map((o, i) => ({ ...o, key: `o${i}`, checked: true })));
      setPhase('review');
    },
  });

  // --- Commit the selection through the normal endpoints ----------------------
  const saveM = useMutation({
    mutationFn: async () => {
      let saved = 0;
      let skipped = 0;
      let failed = 0;
      for (const r of adminRows.filter((r) => r.checked)) {
        try {
          const out = await api.post<{ idempotent?: boolean }>('/medications/administrations', {
            orderId: r.orderId,
            scheduledDate: r.date,
            scheduledTime: r.time || undefined,
            status: r.status,
            actualDate: r.date,
            actualTime: r.time || undefined,
            doseGiven: r.doseGiven || undefined,
            doseUnit: r.doseUnit || undefined,
            note: r.note || undefined,
            clientId: `sheet:${attachmentId}:${r.orderId}:${r.date}:${r.time ?? 'x'}`,
          });
          if (out.idempotent) skipped += 1;
          else saved += 1;
        } catch (e) {
          // 409 = a dose already logged for this slot → a skip, never an overwrite.
          if (e instanceof ApiError && e.status === 409) skipped += 1;
          else failed += 1;
        }
      }
      for (const r of weightRows.filter((r) => r.checked)) {
        try {
          await api.post('/growth', {
            date: r.date,
            weightGrams: String(r.weightGrams),
            weightSource: 'parent',
            weightVerified: false,
            note: r.note || undefined,
          });
          saved += 1;
        } catch {
          failed += 1;
        }
      }
      let obsIndex = 0;
      for (const r of obsRows.filter((r) => r.checked)) {
        try {
          const out = await api.post<{ idempotent?: boolean }>('/observations', {
            date: r.date,
            text: r.text,
            clientId: `sheet:${attachmentId}:obs:${r.date}:${obsIndex}`,
          });
          if (out.idempotent) skipped += 1;
          else saved += 1;
        } catch {
          failed += 1;
        }
        obsIndex += 1;
      }
      return { saved, skipped, failed };
    },
    onSuccess: (res) => {
      setResult(res);
      setPhase('done');
      void qc.invalidateQueries({ queryKey: ['med-grid'] });
      void qc.invalidateQueries({ queryKey: ['growth'] });
      void qc.invalidateQueries({ queryKey: ['observations'] });
      void qc.invalidateQueries({ queryKey: ['journal'] });
    },
  });

  const extractError = extractM.error instanceof ApiError ? extractM.error : null;
  const aiOff = extractError?.code === 'ai_disabled' || extractError?.code === 'ai_unavailable';
  const busy = extractM.isPending || saveM.isPending;
  const checkedCount = adminRows.filter((r) => r.checked).length + weightRows.filter((r) => r.checked).length + obsRows.filter((r) => r.checked).length;
  const nothingFound = phase === 'review' && adminRows.length === 0 && weightRows.length === 0 && obsRows.length === 0;

  // --- Render ------------------------------------------------------------------
  return (
    <div className={className}>
      {/* Phase: pick a file */}
      {phase === 'pick' && (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">{tr(L.hint)}</p>
          <p className="text-xs text-slate-400">{tr(L.aiPrivacy)}</p>

          {file === null ? (
            <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 px-4 py-6 text-center cursor-pointer transition">
              <span className="text-2xl" aria-hidden>📷</span>
              <span className="text-sm text-brand underline">{tr(L.pick)}</span>
              <input type="file" accept="application/pdf,image/*" onChange={(e) => { acceptFile(e.target.files?.[0]); e.target.value = ''; }} className="hidden" />
            </label>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <span className="text-lg" aria-hidden>🗒️</span>
              <span className="flex-1 truncate">{file.name}</span>
              {!busy && (
                <button type="button" className="text-xs underline text-slate-500" onClick={() => setFile(null)}>
                  {tr(L.cancel)}
                </button>
              )}
            </div>
          )}
          {fileError && <p className="text-sm text-danger">{fileError}</p>}

          {extractM.isError && (
            <p className="text-sm text-danger">{aiOff ? tr(L.aiOff) : extractError?.message ?? t('common.error')}</p>
          )}

          <Button type="button" disabled={busy || file === null} onClick={() => file && extractM.mutate(file)}>
            {extractM.isPending ? tr(L.analyzing) : tr(L.analyze)}
          </Button>
        </div>
      )}

      {/* Phase: review + edit */}
      {phase === 'review' && (
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">{tr(L.reviewTitle)}</h3>
            <p className="text-xs text-slate-500">{tr(L.reviewHint)}</p>
          </div>

          {nothingFound && <p className="text-sm text-slate-500">{tr(L.nothingFound)}</p>}

          {adminRows.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{tr(L.medsSection)}</h4>
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                {adminRows.map((r) => (
                  <li key={r.key} className={`flex flex-wrap items-center gap-2 px-3 py-2 text-sm ${r.checked ? 'bg-white' : 'bg-slate-50 opacity-70'}`}>
                    <input
                      type="checkbox"
                      checked={r.checked}
                      onChange={(e) => setAdminRows((rows) => rows.map((x) => (x.key === r.key ? { ...x, checked: e.target.checked } : x)))}
                    />
                    <span className="font-medium text-slate-700 min-w-28">{medLabel(r.orderId, r.medicationLabel)}</span>
                    <span className="tabular-nums text-slate-500">{r.date}</span>
                    <input
                      type="time"
                      value={r.time ?? ''}
                      aria-label={tr(L.time)}
                      onChange={(e) => setAdminRows((rows) => rows.map((x) => (x.key === r.key ? { ...x, time: e.target.value || undefined } : x)))}
                      className="border border-slate-200 rounded px-1 py-0.5 text-xs"
                    />
                    <select
                      value={r.status}
                      onChange={(e) => setAdminRows((rows) => rows.map((x) => (x.key === r.key ? { ...x, status: e.target.value } : x)))}
                      className="border border-slate-200 rounded px-1 py-0.5 text-xs"
                    >
                      {EDITABLE_STATUSES.map((s) => (
                        <option key={s} value={s}>{t(`admin.${s}`)}</option>
                      ))}
                    </select>
                    <input
                      inputMode="decimal"
                      value={r.doseGiven ?? ''}
                      placeholder={tr(L.dose)}
                      aria-label={tr(L.dose)}
                      onChange={(e) => setAdminRows((rows) => rows.map((x) => (x.key === r.key ? { ...x, doseGiven: e.target.value || undefined } : x)))}
                      className="w-16 border border-slate-200 rounded px-1 py-0.5 text-xs tabular-nums"
                    />
                    {r.doseUnit && <span className="text-xs text-slate-400">{r.doseUnit}</span>}
                    {r.duplicate && <Badge tone="warn">{tr(L.alreadyLogged)}</Badge>}
                    {r.note && <span className="text-xs text-slate-400 w-full pl-6">{r.note}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {weightRows.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{tr(L.weightsSection)}</h4>
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                {weightRows.map((r) => (
                  <li key={r.key} className={`flex flex-wrap items-center gap-2 px-3 py-2 text-sm ${r.checked ? 'bg-white' : 'bg-slate-50 opacity-70'}`}>
                    <input
                      type="checkbox"
                      checked={r.checked}
                      onChange={(e) => setWeightRows((rows) => rows.map((x) => (x.key === r.key ? { ...x, checked: e.target.checked } : x)))}
                    />
                    <span className="tabular-nums text-slate-500">{r.date}</span>
                    <input
                      inputMode="decimal"
                      value={String(r.weightGrams / 1000)}
                      aria-label={t('growth.weight')}
                      onChange={(e) => {
                        const kg = Number(e.target.value.replace(',', '.'));
                        if (Number.isFinite(kg) && kg > 0) {
                          setWeightRows((rows) => rows.map((x) => (x.key === r.key ? { ...x, weightGrams: Math.round(kg * 1000) } : x)));
                        }
                      }}
                      className="w-20 border border-slate-200 rounded px-1 py-0.5 text-xs tabular-nums"
                    />
                    <span className="text-xs text-slate-400">{tr(L.weightKg)}</span>
                    {r.duplicate && <Badge tone="warn">{tr(L.alreadyLogged)}</Badge>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {obsRows.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{tr(L.notesSection)}</h4>
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                {obsRows.map((r) => (
                  <li key={r.key} className={`flex items-start gap-2 px-3 py-2 text-sm ${r.checked ? 'bg-white' : 'bg-slate-50 opacity-70'}`}>
                    <input
                      type="checkbox"
                      checked={r.checked}
                      onChange={(e) => setObsRows((rows) => rows.map((x) => (x.key === r.key ? { ...x, checked: e.target.checked } : x)))}
                    />
                    <span className="tabular-nums text-slate-500 whitespace-nowrap">{r.date}</span>
                    <span className="flex-1 text-slate-700">{r.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {unmatched.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{tr(L.unmatchedSection)}</h4>
              <ul className="text-xs text-slate-400 space-y-0.5">
                {unmatched.map((u, i) => (
                  <li key={i}>
                    {u.medication}
                    {u.date ? ` · ${u.date}` : ''}
                    {u.reason === 'future_date' ? ` (${tr(L.futureDate)})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {saveM.isError && <p className="text-sm text-danger">{t('common.error')}</p>}

          <div className="flex items-center gap-2">
            <Button type="button" disabled={busy || checkedCount === 0} onClick={() => saveM.mutate()}>
              {saveM.isPending ? tr(L.saving) : `${tr(L.save)} (${checkedCount})`}
            </Button>
            <Button type="button" variant="secondary" disabled={busy} onClick={resetAll}>
              {tr(L.cancel)}
            </Button>
          </div>
        </div>
      )}

      {/* Phase: done */}
      {phase === 'done' && result && (
        <div className="space-y-3">
          <div className="text-sm rounded-lg px-3 py-2 bg-green-50 border border-green-200 text-green-800" role="status">
            ✓ {result.saved} {tr(L.savedSummary)}
            {result.skipped > 0 ? ` · ${result.skipped} ${tr(L.skippedSummary)}` : ''}
            {result.failed > 0 ? ` · ${result.failed} ${tr(L.failedSummary)}` : ''}
          </div>
          <Button type="button" variant="secondary" onClick={resetAll}>
            {tr(L.startOver)}
          </Button>
        </div>
      )}
    </div>
  );
}
